import { Alert, Col, Flex, Form, Input, Modal, Row, Select, Space, Table, Tag } from 'antd';
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Breadcrumb, Button, Container } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { ControlPanelManager } from '../../../api/ControlPanelManager';
import { useToasts } from '../../../contexts/toasts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import IconButton from '../../../components/common/IconButton';
import AppSearch from '../../../components/common/AppSearch';
import { useFullScreenLoader } from '../../../contexts/fullScreenLoader';
import { Client, ClientRole, Group, Permission, PermissionRoles } from '../../../models/ControlPanel';
import { Role } from '../../../models/User';

function calculateRoles(group: Group) {
  if (!group) return [];

  let clientRoles: ClientRole[] = [];
  if (group.clientRoles) {
    Object.entries(group.clientRoles).forEach(([clientId, roles]) => {
      roles.forEach(name => clientRoles.push({ clientId, name }))
    });
  }
  if (group.realmRoles) clientRoles.push(...group.realmRoles);
  return clientRoles;
}

export default function PermissionsList() {
  const [searchText, setSearchText] = useState('');
  const [permissions, setPermissions] = useState<Group[]>();
  const [selectedClient, setSelectedClient] = useState<string>();
  const [selectedRoles, setSelectedRoles] = useState<string[]>();
  const [permission, setPermission] = useState<Group>();
  const [editingPermission, setEditingPermission] = useState<Permission>(new Permission());
  const [clients, setClients] = useState<Client[]>();
  const [roles, setRoles] = useState<Role[]>();
  const [error, setError] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();

  const client = useMemo(() => clients?.find(c => c.id === selectedClient), [clients, selectedClient]);
  const rolesOptions = useMemo(() => {
    const filtered = roles?.filter(r => {
      const matchingRoles = editingPermission.roles.filter(v => (selectedClient && typeof v === 'object') || (!selectedClient && typeof v === 'string'));
      return selectedClient ? !(matchingRoles as ClientRole[]).map(cr => cr.name).includes(r.name) : !matchingRoles.includes(r.name);
    });
    return filtered?.map(r => ({ label: r.name, value: r.name }));
  }, [roles, editingPermission, selectedClient]);

  const getClients = useCallback(() => {
    ControlPanelManager.getClients()
      .then(
        ({ data }) => setClients(data),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('clients-error', message, `Clients Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [showToast]);

  useEffect(() => {
    getPermissions();
    getClients();
    // eslint-disable-next-line
  }, []);

  const filteredPermissions = useMemo(() => permissions?.filter(p => p.name.toLowerCase().includes(searchText.toLowerCase())), [searchText, permissions]);
  const reset = () => {
    setPermissions(undefined);
    getPermissions();
  }
  const openDeleteModal = (permission: Group) => () => {
    setPermission(permission);
    setShowDeleteModal(true);
  }
  const handleDeleteModalClose = () => {
    setPermission(undefined);
    setShowDeleteModal(false);
  }
  const handleEditModalClose = () => {
    setShowEditModal(false);
    setEditingPermission(new Permission());
  }
  const openCreateModal = () => setShowEditModal(true);

  const deletePermission = () => {
    showLoader();

    ControlPanelManager.deleteGroup(permission!.id as string)
      .then(
        () => {
          hideLoader();
          showToast('permission-success', 'Permission deleted successfully', 'Permission Success', 'success');
          reset();
          handleDeleteModalClose();
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('permission-error', message, `Permission Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const getPermissions = () => {
    ControlPanelManager.getPermissions()
      .then(
        ({ data }) => setPermissions(data),
        (err) => {
          setError(true);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('permissions-error', message, `Permissions Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  useEffect(() => {
    setRoles(undefined);
    if (selectedClient) getClientRoles();
    else getRealmRoles();
    // eslint-disable-next-line
  }, [selectedClient]);

  const getRealmRoles = () => {
    ControlPanelManager.getRoles()
      .then(
        ({ data }) => setRoles(data),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('roles-error', message, `Roles Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const getClientRoles = () => {
    ControlPanelManager.getClientRoles(selectedClient as string)
      .then(
        ({ data }) => setRoles(data),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('roles-error', message, `Roles Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const submitPermission = () => {
    showLoader();

    const body = {
      ...editingPermission,
      roles: editingPermission.roles.reduce((acc, curr) => {
        if (typeof curr === 'string') acc.realmRoles.push(curr);
        else {
          if (acc.clientRoles[curr.clientId]) acc.clientRoles[curr.clientId].push(curr.name);
          else acc.clientRoles[curr.clientId] = [curr.name];
        }
        return acc;
      }, { clientRoles: {}, realmRoles: [] } as PermissionRoles),
    }

    const promise = editingPermission.id !== undefined
      ? ControlPanelManager.updatePermission(body)
      : ControlPanelManager.createPermission(body);

    promise.then(
      () => {
        reset();
        hideLoader();
        handleEditModalClose();
        showToast('permission-success', `Permission ${editingPermission.id !== undefined ? 'updated' : 'created'} successfully`, 'Permission Success', 'success');
      },
      (err) => {
        hideLoader();
        const message = err.response?.data?.message || err.message;
        const status = err.response?.status;
        showToast('permission-error', message, `Permission Error ${status ? `(${status})` : ''}`, 'danger');
      }
    )
  }

  const handleFieldChange = useCallback((evt: React.ChangeEvent<HTMLInputElement>) => {
    const value = evt.target.value;
    const field = evt.target.name;
    setEditingPermission(p => ({
      ...p!,
      [field]: value
    }));
  }, []);

  const removeRole = useCallback((index: number) => () => {
    setEditingPermission(p => {
      const newRoles = [...p.roles];
      newRoles.splice(index, 1);
      return { ...p, roles: newRoles };
    });
  }, []);

  const handleEdit = useCallback((record: Group) => () => {
    const roles = calculateRoles(record);
    setEditingPermission({ id: record.id, name: record.name, roles });
    setShowEditModal(true);
  }, []);

  const handleAddRole = useCallback(() => {
    const newRoles = selectedClient ? selectedRoles!.map(role => ({ clientId: client!.clientId, name: role! })) : selectedRoles!;
    setEditingPermission(p => ({ ...p!, roles: p!.roles.concat(newRoles) }));
    setSelectedRoles(undefined);
    setSelectedClient(undefined);
  }, [selectedClient, selectedRoles, client]);

  const renderAsyncContent = useCallback(() => {
    if (error) return (
      <Alert
        type="error"
        showIcon
        message="An error occurred while retrieving permissions. Try again later."
      />
    )

    return (
      <Table size="middle" scroll={{ y: 'calc(100vh - 300px)' }} dataSource={filteredPermissions} rowKey="id">
        <Table.Column
          key="name"
          dataIndex="name"
          title="Name"
          width="30%"
        />
        <Table.Column
          key="roles"
          title="Roles"
          render={(_value, record: Group) => (
            calculateRoles(record).map((role, index) => (
              <Tag color="geekblue" key={index}>
                {typeof role === 'object' && <b className="font-xs me-1">{role.clientId}</b>}
                <span>{typeof role === 'object' ? role.name : role}</span>
              </Tag>
            ))
          )}
        />
        <Table.Column
          key="actions"
          title="Actions"
          className="text-center"
          width="20%"
          render={(_value, record: any) => (
            <Space size={1}>
              <IconButton tooltip="Edit" onClick={handleEdit(record)}>
                <FontAwesomeIcon fixedWidth icon={solid('pen')} />
              </IconButton>
              <IconButton tooltip="Delete" className="text-danger ms-2" onClick={openDeleteModal(record)}>
                <FontAwesomeIcon fixedWidth icon={solid('trash')} />
              </IconButton>
            </Space>
          )}
        />
      </Table>
    )
  }, [error, filteredPermissions, handleEdit]);

  return (
    <Container className="my-3">
      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/control-panel" }}>Control Panel</Breadcrumb.Item>
        <Breadcrumb.Item active>Permission Management</Breadcrumb.Item>
      </Breadcrumb>

      <Row className="mb-3" align="middle">
        <Col md={6}>
          <h5 className="mb-0">Permissions List</h5>
        </Col>

        <Col flex="1 0" className="text-center px-3">
          <AppSearch placeholder="Search permission..." value={searchText} onChange={setSearchText} />
        </Col>

        <Col md={6} className="text-end">
          <Button size="sm" variant="success" onClick={openCreateModal}>
            <FontAwesomeIcon icon={solid('plus')} />
            <span className="ms-2">Register Permission</span>
          </Button>
        </Col>
      </Row>

      {renderAsyncContent()}

      <Modal
        title="Delete Permission"
        open={showDeleteModal}
        onCancel={handleDeleteModalClose}
        afterClose={handleDeleteModalClose}
        footer={
          <Flex justify="space-between" className="mt-3">
            <Button size="sm" variant="light" className="border" onClick={handleDeleteModalClose}>Cancel</Button>
            <Button size="sm" variant="danger" onClick={deletePermission}>
              <FontAwesomeIcon icon={solid('trash')} />
              <span className="ms-2">Delete</span>
            </Button>
          </Flex>
        }
      >
        <p>Are you sure you want to delete permission <b>{permission?.name}</b>?</p>

        <Alert type="warning" showIcon message="Remember, this operation cannot be undone" />
      </Modal>

      <Modal
        title={`${editingPermission?.id ? 'Edit' : 'Create'} Permission`}
        open={showEditModal}
        onCancel={handleEditModalClose}
        afterClose={handleEditModalClose}
        footer={null}
      >
        <Form className="mt-3" layout="vertical" onFinish={submitPermission}>
          <Form.Item label="Name" rules={[{ required: true, message: 'Permission Name is required' }]}>
            <Input name="name" value={editingPermission?.name} onChange={handleFieldChange} placeholder="Permission Name" />
          </Form.Item>

          <h6 className="mb-1">Permission Roles</h6>
          {editingPermission?.roles.length === 0 && (
            <Alert type="info" showIcon message="No roles added to this permission yet." />
          )}

          {editingPermission?.roles.map((role, index) => (
            <Tag color="geekblue" key={index} className="pe-1 mb-1">
              {typeof role === 'object' && <b className="font-xs me-1">{role.clientId}</b>}
              <span>{typeof role === 'object' ? role.name : role}</span>

              <IconButton className="ms-1 p-1" type="button" onClick={removeRole(index)}>
                <FontAwesomeIcon icon={solid('times')} />
              </IconButton>
            </Tag>
          ))}

          <Flex className="mt-3" align="end">
            <Col className="pe-1" md={8}>
              <Form.Item label="Client" className="mb-0">
                <Select
                  className="w-100"
                  placeholder="Select Client"
                  value={selectedClient}
                  onChange={setSelectedClient}
                  options={clients
                    ?.filter(c => (window.__env.ALLOWED_CLIENT_ROLE_CLIENTS || []).includes(c.clientId))
                    .map(c => ({ label: c.clientId, value: c.id }))
                  }
                  loading={!clients}
                  allowClear
                  disabled={selectedRoles !== undefined && selectedRoles.length > 0}
                />
              </Form.Item>
            </Col>
            <Col className="ps-1" flex="auto">
              <Form.Item label="Roles" className="mb-0">
                <Select
                  mode="multiple"
                  className="w-100"
                  placeholder="Select role"
                  value={selectedRoles}
                  onChange={setSelectedRoles}
                  options={rolesOptions}
                  loading={!roles}
                  showSearch
                  allowClear
                />
              </Form.Item>
            </Col>

            <IconButton type="button" disabled={!selectedRoles} className="ms-2" onClick={handleAddRole}>Add</IconButton>
          </Flex>

          <br />

          <Flex justify="space-between">
            <Button size="sm" type="button" variant="light" className="border" onClick={handleEditModalClose}>Cancel</Button>
            <Button size="sm" type="submit" variant="success">
              <FontAwesomeIcon icon={solid('save')} />
              <span className="ms-2">Confirm</span>
            </Button>
          </Flex>
        </Form>
      </Modal>
    </Container>
  )
}
